import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState, PageHeader, Section } from '@/components/page';
import { api } from '@/lib/api';
import { can, meQuery } from '@/lib/auth';
import { DETAIL_LABEL, PARTY_LABEL, SOURCE_LABEL } from '@/lib/labels';
import type { Stats } from '@/lib/types';
import { cn } from '@/lib/utils';

const RANGES = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '30d', label: '30 days', days: 30 },
  { value: '90d', label: '90 days', days: 90 },
  { value: '12m', label: '12 months', days: 365 },
] as const;
type Range = (typeof RANGES)[number]['value'];

export const Route = createFileRoute('/_app/stats')({
  validateSearch: (s: Record<string, unknown>): { range?: Range } => ({
    range: RANGES.some((r) => r.value === s.range) ? (s.range as Range) : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me || !can.readStats(me)) throw redirect({ to: '/entries', search: {} });
  },
  component: StatsPage,
});

function StatsPage() {
  const { range = '30d' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const days = RANGES.find((r) => r.value === range)!.days;

  const query = useQuery({
    queryKey: ['stats', range],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - days * 86_400_000);
      return api.get<Stats>('/v1/stats', { from: from.toISOString(), to: to.toISOString() });
    },
  });

  return (
    <>
      <PageHeader
        title="Stats"
        description="What’s been added to the register, what happened to it, and how often checks found a match."
        actions={
          <div role="radiogroup" aria-label="Time range" className="inline-flex rounded-lg border bg-card p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                role="radio"
                aria-checked={r.value === range}
                onClick={() => void navigate({ search: { range: r.value === '30d' ? undefined : r.value } })}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  r.value === range && 'bg-accent text-accent-foreground hover:text-accent-foreground',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !query.data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <StatsBody stats={query.data} rangeLabel={RANGES.find((r) => r.value === range)!.label} />
      )}
    </>
  );
}

function StatsBody({ stats, rangeLabel }: { stats: Stats; rangeLabel: string }) {
  const sum = (o: Partial<Record<string, number>>) => Object.values(o).reduce<number>((a, b) => a + (b ?? 0), 0);
  const casesCreated = sum(stats.cases.createdBySource);
  const entriesAdded = sum(stats.entries.addedByParty);
  const matchRate = stats.lookups.total ? Math.round((stats.lookups.withMatches / stats.lookups.total) * 100) : null;

  return (
    <div className="grid gap-5">
      <section aria-label="Right now" className="grid gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">Right now</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Active entries" value={stats.current.activeEntries} note="Matched by lookups" />
          <Tile label="Expiring in 30 days" value={stats.current.expiringWithin30Days} note="Six-year retention" />
        </div>
      </section>
      <section aria-label={`Last ${rangeLabel}`} className="grid gap-3">
        <h2 className="text-sm font-medium text-muted-foreground">Last {rangeLabel}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Cases recorded" value={casesCreated} />
          <Tile label="Entries added" value={entriesAdded} />
          <Tile label="Cases reversed" value={stats.cases.reversed} note={`${stats.entries.ended} entries ended, ${stats.entries.removed} removed`} />
          <Tile
            label="Lookups"
            value={stats.lookups.total}
            note={matchRate === null ? 'No checks yet' : `${stats.lookups.withMatches.toLocaleString()} matched (${matchRate}%)`}
          />
        </div>
      </section>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        <BarList title="Entries added by party" rows={Object.entries(PARTY_LABEL).map(([k, label]) => ({ label, value: stats.entries.addedByParty[k as keyof typeof PARTY_LABEL] ?? 0 }))} />
        <BarList title="Entries added by detail" rows={Object.entries(DETAIL_LABEL).map(([k, label]) => ({ label, value: stats.entries.addedByDetailType[k as keyof typeof DETAIL_LABEL] ?? 0 }))} />
        <BarList title="Cases by source" rows={Object.entries(SOURCE_LABEL).map(([k, label]) => ({ label, value: stats.cases.createdBySource[k as keyof typeof SOURCE_LABEL] ?? 0 }))} />
      </div>
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-1 text-3xl font-semibold tracking-tight tabular-nums">{value.toLocaleString()}</div>
      {note ? <div className="mt-1 text-xs text-muted-foreground">{note}</div> : null}
    </div>
  );
}

/**
 * One-series horizontal bars: thin, rounded at the data end, value labelled
 * in text ink beside each bar (the labels double as the table view).
 */
function BarList({ title, rows }: { title: string; rows: { label: string; value: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((a, r) => a + r.value, 0);
  return (
    <Section title={title}>
      <ul className="grid gap-2.5 p-5" aria-label={title}>
        {rows.map((r) => (
          <li
            key={r.label}
            className="group grid grid-cols-[96px_minmax(0,1fr)_48px] items-center gap-3 rounded-md text-sm"
            title={`${r.label}: ${r.value.toLocaleString()}${total ? ` (${Math.round((r.value / total) * 100)}%)` : ''}`}
          >
            <span className="truncate text-muted-foreground">{r.label}</span>
            <span className="relative h-2.5 rounded-r bg-muted" aria-hidden="true">
              <span
                className="absolute inset-y-0 left-0 rounded-r-[4px] bg-chart-1 transition-opacity group-hover:opacity-80"
                style={{ width: r.value === 0 ? 0 : `max(4px, ${(r.value / max) * 100}%)` }}
              />
            </span>
            <span className="text-right tabular-nums">{r.value.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
